<template>
  <div class="music-piece-builder" dir="rtl">
    <v-container fluid>
      <v-card class="pa-4 elevation-2 page-card">
        <v-card-title class="justify-space-between align-center flex-wrap">
          <div class="page-title">ساخت قطعه موسیقی نقاره</div>
          <div class="d-flex align-center flex-wrap">
            <v-btn
              class="ml-2 mb-2"
              color="secondary"
              depressed
              @click="resetWizard"
            >
              شروع دوباره
            </v-btn>
            <v-btn
              class="mb-2"
              color="primary"
              :disabled="!canSubmit || saving"
              depressed
              dark
              @click="createPiece"
            >
              <v-progress-circular
                v-if="saving"
                indeterminate
                color="white"
                size="20"
                width="2"
                class="ml-2"
              />
              ذخیره قطعه
            </v-btn>
          </div>
        </v-card-title>

        <v-divider class="mb-6"></v-divider>

        <v-stepper v-model="step" class="transparent-stepper" alt-labels>
          <v-stepper-header>
            <v-stepper-step :complete="step > 1" step="1">
              انتخاب یا ساخت درس
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :complete="step > 2" step="2">
              چیدمان الگو
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step step="3">
              مرور و نهایی‌سازی
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-4 selection-card">
                    <div class="card-title mb-4">انتخاب درس موجود</div>
                    <v-alert
                      border="left"
                      colored-border
                      elevation="2"
                      type="info"
                      class="mb-4"
                    >
                      اگر درس و الگوی مرجع از قبل تعریف شده‌اند، از این بخش انتخاب کنید تا بتوانید الگو را بازآفرینی یا به‌روزرسانی کنید.
                    </v-alert>
                    <v-select
                      v-model="selectedLessonId"
                      :items="lessonOptions"
                      :loading="loadingLessons"
                      item-text="title"
                      item-value="id"
                      label="درس‌های موجود"
                      dense
                      outlined
                      hide-details="auto"
                      clearable
                      :return-object="false"
                    ></v-select>
                    <v-btn
                      class="mt-4"
                      color="primary"
                      depressed
                      block
                      :disabled="!selectedLessonId || loadingLessonDetail"
                      @click="loadLessonDetail"
                    >
                      <v-progress-circular
                        v-if="loadingLessonDetail"
                        indeterminate
                        size="20"
                        width="2"
                        color="white"
                        class="ml-2"
                      />
                      بارگذاری مشخصات درس
                    </v-btn>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-4 selection-card primary-card">
                    <div class="card-title mb-4">ساخت درس تازه</div>
                    <v-alert
                      border="left"
                      colored-border
                      elevation="2"
                      type="success"
                      class="mb-4"
                    >
                      همه گزینه‌ها از طریق منوهای کشویی انتخاب می‌شوند و عنوان درس به صورت خودکار ساخته خواهد شد.
                    </v-alert>
                    <v-select
                      v-model="lessonForm.groove"
                      :items="grooveOptions"
                      item-text="title"
                      item-value="value"
                      label="نوع قطعه"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="lessonForm.tempo"
                      :items="tempoOptions"
                      label="تمپو (BPM)"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="lessonForm.timeSignature"
                      :items="timeSignatureOptions"
                      label="کسر میزان"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="lessonForm.beatsPerMeasure"
                      :items="beatCountOptions"
                      label="تعداد ضرب در هر میزان"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="lessonForm.subdivisionPerBeat"
                      :items="subdivisionOptions"
                      label="ریز تقسیم هر ضرب"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="lessonForm.swingRatio"
                      :items="swingOptions"
                      label="حس اجرا"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-chip
                      v-if="computedLessonTitle"
                      class="mt-4"
                      color="white"
                      text-color="primary"
                      outlined
                      label
                    >
                      عنوان پیشنهادی درس: {{ computedLessonTitle }}
                    </v-chip>
                  </v-card>
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-6">
                <v-btn
                  color="primary"
                  depressed
                  :disabled="!canGoToPattern"
                  @click="step = 2"
                >
                  مرحله بعد
                  <v-icon right>mdi-arrow-left</v-icon>
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content step="2">
              <v-row dense>
                <v-col cols="12" md="4">
                  <v-card outlined class="pa-4 selection-card">
                    <div class="card-title mb-4">چارچوب الگو</div>
                    <v-select
                      v-model="patternForm.blueprint"
                      :items="patternBlueprintOptions"
                      item-text="title"
                      item-value="value"
                      label="انتخاب الگوی پیشنهادی"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-alert
                      v-if="selectedBlueprint"
                      border="left"
                      colored-border
                      type="info"
                      class="mt-4"
                    >
                      {{ selectedBlueprint.description }}
                    </v-alert>
                    <v-select
                      v-model="patternForm.lengthMeasures"
                      :items="measureLengthOptions"
                      label="تعداد میزان"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="patternForm.accentProfile"
                      :items="accentProfiles"
                      item-text="title"
                      item-value="value"
                      label="پروفایل تاکید"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-select
                      v-model="patternForm.velocityProfile"
                      :items="velocityProfiles"
                      item-text="title"
                      item-value="value"
                      label="دینامیک اجرا"
                      dense
                      outlined
                      hide-details="auto"
                    ></v-select>
                    <v-btn
                      class="mt-6"
                      color="primary"
                      block
                      depressed
                      :disabled="!patternForm.blueprint"
                      @click="generateEventsFromBlueprint"
                    >
                      تولید خودکار رویدادها
                    </v-btn>
                    <v-chip
                      v-if="computedPatternName"
                      class="mt-4"
                      color="white"
                      text-color="primary"
                      outlined
                      label
                    >
                      نام پیشنهادی الگو: {{ computedPatternName }}
                    </v-chip>
                  </v-card>
                </v-col>
                <v-col cols="12" md="8">
                  <v-card outlined class="pa-4 selection-card">
                    <div class="card-title mb-4">چیدمان ضرب‌ها</div>
                    <v-alert
                      type="success"
                      border="left"
                      colored-border
                      class="mb-4"
                    >
                      برای افزودن یا ویرایش هر ضرب از منوهای انتخابی استفاده کنید. رویدادها با ترتیب اجرای نهایی نمایش داده می‌شوند.
                    </v-alert>
                    <v-card class="event-composer" outlined>
                      <v-card-text>
                        <v-row dense>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.measure"
                              :items="measureOptions"
                              label="میزان"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.beat"
                              :items="beatOptions"
                              label="ضرب"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.subdivision"
                              :items="subdivisionChoices"
                              label="ریز تقسیم"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.strokeType"
                              :items="strokeTypeOptions"
                              label="نوع ضربه"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.accented"
                              :items="accentOptions"
                              label="تاکید"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.rollType"
                              :items="rollTypeOptions"
                              item-text="title"
                              item-value="value"
                              label="تزئین"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.expectedVelocity"
                              :items="velocityOptions"
                              label="شدت صدا"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="6" md="3">
                            <v-select
                              v-model="eventComposer.duration"
                              :items="durationOptions"
                              label="مدت (ms)"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="3">
                            <v-select
                              v-model="eventComposer.offset"
                              :items="offsetOptions"
                              label="جابه‌جایی زمانی"
                              dense
                              outlined
                              hide-details="auto"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="9" class="d-flex align-center">
                            <v-btn
                              color="primary"
                              depressed
                              @click="addEventFromComposer"
                            >
                              افزودن به لیست ضرب‌ها
                              <v-icon right>mdi-plus-circle</v-icon>
                            </v-btn>
                            <div class="ml-3 muted-text">
                              (رویداد جدید به انتهای لیست افزوده می‌شود)
                            </div>
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>

                    <div class="event-table-wrapper mt-6">
                      <v-simple-table dense class="event-table">
                        <thead>
                        <tr>
                          <th class="text-center">#</th>
                          <th class="text-center">میزان</th>
                          <th class="text-center">ضرب</th>
                          <th class="text-center">ریز تقسیم</th>
                          <th class="text-center">نوع ضربه</th>
                          <th class="text-center">تاکید</th>
                          <th class="text-center">تزئین</th>
                          <th class="text-center">شدت</th>
                          <th class="text-center">مدت</th>
                          <th class="text-center">جابه‌جایی</th>
                          <th class="text-center">عملیات</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="!patternForm.events.length">
                          <td colspan="11" class="text-center empty-state">
                            هنوز رویدادی ثبت نشده است. از الگوهای پیشنهادی یا بخش افزودن استفاده کنید.
                          </td>
                        </tr>
                        <tr v-for="(event, index) in patternForm.events" :key="index">
                          <td class="text-center">{{ index + 1 }}</td>
                          <td class="text-center">
                            <v-select
                              v-model="event.measure"
                              :items="measureOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.beat"
                              :items="beatOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.subdivision"
                              :items="subdivisionChoices"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.stroke_type"
                              :items="strokeTypeOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.accented"
                              :items="accentOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.roll_type"
                              :items="rollTypeOptions"
                              item-text="title"
                              item-value="value"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.expected_velocity"
                              :items="velocityOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.duration_ms"
                              :items="durationOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center">
                            <v-select
                              v-model="event.time_offset_ms"
                              :items="offsetOptions"
                              dense
                              hide-details
                              outlined
                            ></v-select>
                          </td>
                          <td class="text-center action-cell">
                            <v-btn icon small @click="moveEventUp(index)" :disabled="index === 0">
                              <v-icon small>mdi-arrow-up</v-icon>
                            </v-btn>
                            <v-btn icon small @click="moveEventDown(index)" :disabled="index === patternForm.events.length - 1">
                              <v-icon small>mdi-arrow-down</v-icon>
                            </v-btn>
                            <v-btn icon small @click="removeEvent(index)">
                              <v-icon small color="error">mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
              <div class="d-flex justify-space-between mt-6">
                <v-btn color="secondary" depressed @click="step = 1">
                  <v-icon left>mdi-arrow-right</v-icon>
                  بازگشت به مرحله قبل
                </v-btn>
                <v-btn
                  color="primary"
                  depressed
                  :disabled="!patternForm.events.length"
                  @click="step = 3"
                >
                  مرحله بعد
                  <v-icon right>mdi-arrow-left</v-icon>
                </v-btn>
              </div>
            </v-stepper-content>

            <v-stepper-content step="3">
              <v-row>
                <v-col cols="12" md="5">
                  <v-card outlined class="pa-4 selection-card">
                    <div class="card-title mb-4">خلاصه درس</div>
                    <summary-item label="عنوان درس" :value="computedLessonTitle || selectedLessonTitle"></summary-item>
                    <summary-item label="تمپو" :value="lessonForm.tempo ? lessonForm.tempo + ' BPM' : '-' "></summary-item>
                    <summary-item label="کسر میزان" :value="lessonForm.timeSignature || '-' "></summary-item>
                    <summary-item label="تعداد ضرب" :value="lessonForm.beatsPerMeasure || '-' "></summary-item>
                    <summary-item label="ریز تقسیم" :value="lessonForm.subdivisionPerBeat || '-' "></summary-item>
                    <summary-item label="حس اجرا" :value="swingLabel"></summary-item>
                  </v-card>
                </v-col>
                <v-col cols="12" md="7">
                  <v-card outlined class="pa-4 selection-card">
                    <div class="card-title mb-4">خلاصه الگو</div>
                    <summary-item label="نام الگو" :value="computedPatternName"></summary-item>
                    <summary-item label="تعداد میزان" :value="patternForm.lengthMeasures"></summary-item>
                    <summary-item label="الگوی تاکید" :value="accentProfileTitle"></summary-item>
                    <summary-item label="دینامیک" :value="velocityProfileTitle"></summary-item>
                    <summary-item label="تعداد رویداد" :value="patternForm.events.length"></summary-item>
                    <v-divider class="my-4"></v-divider>
                    <div class="timeline-label">پیش‌نمایش توالی</div>
                    <div class="event-preview-wrapper">
                      <div
                        v-for="(event, index) in patternForm.events"
                        :key="index"
                        class="event-preview-chip"
                      >
                        <span class="event-index">{{ index + 1 }}</span>
                        <span class="event-measure">م{{ event.measure }}</span>
                        <span class="event-beat">ض{{ event.beat }}</span>
                        <span class="event-stroke">{{ mapStrokeLabel(event.stroke_type) }}</span>
                        <v-icon v-if="event.accented" small color="warning">mdi-brightness-7</v-icon>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
              <div class="d-flex justify-space-between mt-6">
                <v-btn color="secondary" depressed @click="step = 2">
                  <v-icon left>mdi-arrow-right</v-icon>
                  بازگشت به مرحله قبل
                </v-btn>
                <v-btn
                  color="primary"
                  depressed
                  :disabled="!canSubmit || saving"
                  @click="createPiece"
                >
                  <v-progress-circular
                    v-if="saving"
                    indeterminate
                    size="20"
                    width="2"
                    color="white"
                    class="ml-2"
                  />
                  ذخیره قطعه
                </v-btn>
              </div>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import SummaryItem from "@/views/MachineLearning/components/SummaryItem";

export default {
  name: "MusicPieceBuilder",
  components: { SummaryItem },
  data() {
    return {
      step: 1,
      saving: false,
      loadingLessons: false,
      loadingLessonDetail: false,
      lessonOptions: [],
      selectedLessonId: null,
      selectedLessonTitle: "",
      lessonForm: {
        groove: null,
        tempo: null,
        timeSignature: null,
        beatsPerMeasure: null,
        subdivisionPerBeat: null,
        swingRatio: null,
      },
      patternForm: {
        blueprint: null,
        lengthMeasures: 1,
        accentProfile: "balanced",
        velocityProfile: "medium",
        events: [],
      },
      eventComposer: {
        measure: 1,
        beat: 1,
        subdivision: 0,
        strokeType: "R",
        accented: false,
        rollType: null,
        expectedVelocity: 105,
        duration: 240,
        offset: 0,
      },
      grooveOptions: [
        {
          value: "darbuka-basic",
          title: "داربوکا پایه",
          description: "الگوی راست-چپ برای شروع تمرینات داربوکا",
          defaults: { tempo: 96, timeSignature: "4/4", beats: 4, subdivision: 4 },
        },
        {
          value: "daf-ritmic",
          title: "دف حماسی",
          description: "ریتم‌های کوبه‌ای پرانرژی برای دف",
          defaults: { tempo: 108, timeSignature: "6/8", beats: 6, subdivision: 3 },
        },
        {
          value: "tonbak-flow",
          title: "تنبک نرم",
          description: "سیالیت ملایم برای تکنیک‌های تنبک",
          defaults: { tempo: 84, timeSignature: "4/4", beats: 4, subdivision: 6 },
        },
        {
          value: "latin-groove",
          title: "لاتین شاد",
          description: "الگوهای کوبه‌ای سبک لاتین",
          defaults: { tempo: 120, timeSignature: "4/4", beats: 4, subdivision: 4 },
        },
        {
          value: "fusion-complex",
          title: "فیوزن پیچیده",
          description: "ریتم‌های ترکیبی برای اجراهای پیشرفته",
          defaults: { tempo: 132, timeSignature: "7/8", beats: 7, subdivision: 4 },
        },
      ],
      tempoOptions: [72, 80, 88, 96, 104, 112, 120, 128, 136, 144],
      timeSignatureOptions: ["2/4", "3/4", "4/4", "6/8", "7/8", "9/8"],
      beatCountOptions: [2, 3, 4, 5, 6, 7, 8],
      subdivisionOptions: [2, 3, 4, 6, 8, 12, 16],
      swingOptions: [
        { text: "بدون سوئینگ", value: null },
        { text: "سوئینگ ملایم (60/40)", value: 0.6 },
        { text: "سوئینگ جز (57/43)", value: 0.57 },
        { text: "سوئینگ تریوله (66/33)", value: 0.66 },
      ],
      patternBlueprintOptions: [
        {
          value: "steady-pulse",
          title: "نبض یکنواخت",
          description: "ضربات یکنواخت با تاکید روی ضرب اول هر میزان",
        },
        {
          value: "syncopated-flow",
          title: "سینکوپ نرم",
          description: "افزودن ضرب‌های بینابینی برای ایجاد حس حرکت",
        },
        {
          value: "flam-intro",
          title: "شروع فلام",
          description: "هر میزان با یک فلام آغاز می‌شود و با الگوی راست-چپ ادامه می‌یابد",
        },
        {
          value: "crescendo-roll",
          title: "رول افزایشی",
          description: "حرکت تدریجی از نرم به قوی با رول‌های متناوب",
        },
      ],
      measureLengthOptions: [1, 2, 3, 4, 6, 8],
      accentProfiles: [
        { value: "balanced", title: "متعادل" },
        { value: "strong-downbeat", title: "تاکید روی ضرب اول" },
        { value: "syncopated", title: "تاکید سینکوپ" },
      ],
      velocityProfiles: [
        { value: "medium", title: "متوسط" },
        { value: "dynamic", title: "پویایی زیاد" },
        { value: "soft", title: "ملایم" },
      ],
      strokeTypeOptions: [
        { text: "دست راست", value: "R" },
        { text: "دست چپ", value: "L" },
        { text: "باس", value: "B" },
        { text: "کِلَپ", value: "C" },
      ],
      accentOptions: [
        { text: "بدون تاکید", value: false },
        { text: "تاکید شده", value: true },
      ],
      rollTypeOptions: [
        { title: "بدون تزئین", value: null },
        { title: "فلام", value: "flam" },
        { title: "رول سریع", value: "buzz" },
        { title: "دابل استروک", value: "double" },
      ],
      velocityOptions: [80, 90, 100, 110, 120, 126],
      durationOptions: [160, 220, 260, 320, 380],
      offsetOptions: [
        { text: "دقیق روی ضرب", value: 0 },
        { text: "کمی جلوتر (-15ms)", value: -15 },
        { text: "کمی عقب‌تر (+15ms)", value: 15 },
      ],
    };
  },
  computed: {
    measureOptions() {
      return Array.from({ length: this.patternForm.lengthMeasures || 1 }, (_, i) => i + 1);
    },
    beatOptions() {
      return Array.from({ length: this.lessonForm.beatsPerMeasure || 1 }, (_, i) => i + 1);
    },
    subdivisionChoices() {
      const total = this.lessonForm.subdivisionPerBeat || 1;
      return Array.from({ length: total }, (_, i) => i);
    },
    canGoToPattern() {
      const hasExisting = this.selectedLessonId && this.lessonForm.tempo && this.lessonForm.timeSignature;
      const hasNew =
        this.lessonForm.groove &&
        this.lessonForm.tempo &&
        this.lessonForm.timeSignature &&
        this.lessonForm.beatsPerMeasure &&
        this.lessonForm.subdivisionPerBeat;
      return hasExisting || hasNew;
    },
    canSubmit() {
      return this.patternForm.events.length > 0 && this.canGoToPattern;
    },
    computedLessonTitle() {
      if (!this.lessonForm.groove) {
        return "";
      }
      const groove = this.grooveOptions.find((item) => item.value === this.lessonForm.groove);
      if (!groove) {
        return "";
      }
      const tempo = this.lessonForm.tempo ? `${this.lessonForm.tempo} BPM` : "";
      const signature = this.lessonForm.timeSignature || "";
      return [groove.title, tempo, signature].filter(Boolean).join(" - ");
    },
    computedPatternName() {
      const blueprint = this.patternBlueprintOptions.find((item) => item.value === this.patternForm.blueprint);
      if (!blueprint) {
        return this.selectedLessonTitle || this.computedLessonTitle;
      }
      return `${blueprint.title} ${this.lessonForm.tempo || ""}`.trim();
    },
    swingLabel() {
      const selected = this.swingOptions.find((item) => item.value === this.lessonForm.swingRatio);
      return selected ? selected.text : "بدون سوئینگ";
    },
    selectedBlueprint() {
      return this.patternBlueprintOptions.find((item) => item.value === this.patternForm.blueprint) || null;
    },
    accentProfileTitle() {
      const profile = this.accentProfiles.find((item) => item.value === this.patternForm.accentProfile);
      return profile ? profile.title : "-";
    },
    velocityProfileTitle() {
      const profile = this.velocityProfiles.find((item) => item.value === this.patternForm.velocityProfile);
      return profile ? profile.title : "-";
    },
  },
  watch: {
    "lessonForm.groove"(value) {
      if (!value) {
        return;
      }
      const groove = this.grooveOptions.find((item) => item.value === value);
      if (!groove) {
        return;
      }
      this.lessonForm.tempo = groove.defaults.tempo;
      this.lessonForm.timeSignature = groove.defaults.timeSignature;
      this.lessonForm.beatsPerMeasure = groove.defaults.beats;
      this.lessonForm.subdivisionPerBeat = groove.defaults.subdivision;
      if (!this.tempoOptions.includes(groove.defaults.tempo)) {
        this.tempoOptions.push(groove.defaults.tempo);
        this.tempoOptions.sort((a, b) => a - b);
      }
      this.resetEvents();
      this.syncComposerDefaults();
    },
    "patternForm.lengthMeasures"() {
      if (this.eventComposer.measure > this.patternForm.lengthMeasures) {
        this.eventComposer.measure = 1;
      }
      this.patternForm.events = this.patternForm.events.map((event) => ({
        ...event,
        measure: Math.min(event.measure, this.patternForm.lengthMeasures),
      }));
    },
    "lessonForm.beatsPerMeasure"() {
      if (this.eventComposer.beat > this.lessonForm.beatsPerMeasure) {
        this.eventComposer.beat = 1;
      }
      this.patternForm.events = this.patternForm.events.map((event) => ({
        ...event,
        beat: Math.min(event.beat, this.lessonForm.beatsPerMeasure),
      }));
    },
    "lessonForm.subdivisionPerBeat"() {
      if (this.eventComposer.subdivision >= this.lessonForm.subdivisionPerBeat) {
        this.eventComposer.subdivision = 0;
      }
      this.patternForm.events = this.patternForm.events.map((event) => ({
        ...event,
        subdivision: Math.min(event.subdivision, this.lessonForm.subdivisionPerBeat - 1),
      }));
    },
  },
  created() {
    this.fetchLessons();
  },
  methods: {
    async fetchLessons() {
      this.loadingLessons = true;
      try {
        const response = await this.$axios.get("/admin/lessons", {
          params: { per_page: 100 },
        });
        this.lessonOptions = response.data?.data || [];
      } catch (error) {
        this.openSnackbar("خطا در دریافت فهرست درس‌ها", "#d40d45");
      } finally {
        this.loadingLessons = false;
      }
    },
    async loadLessonDetail() {
      if (!this.selectedLessonId) {
        return;
      }
      this.loadingLessonDetail = true;
      try {
        const { data } = await this.$axios.get(`/admin/lessons/${this.selectedLessonId}`);
        this.selectedLessonTitle = data.title;
        this.lessonForm = {
          groove: null,
          tempo: data.tempo_bpm,
          timeSignature: data.time_signature,
          beatsPerMeasure: data.beats_per_measure,
          subdivisionPerBeat: data.subdivision_per_beat,
          swingRatio: data.grid_settings?.swing_ratio ?? null,
        };
        this.syncComposerDefaults();
        if (data.reference_pattern) {
          this.hydratePatternFromReference(data.reference_pattern);
        } else {
          await this.fetchReferencePattern();
        }
        this.openSnackbar("درس با موفقیت بارگذاری شد");
        this.step = 2;
      } catch (error) {
        this.openSnackbar("بارگذاری درس با خطا مواجه شد", "#d40d45");
      } finally {
        this.loadingLessonDetail = false;
      }
    },
    async fetchReferencePattern() {
      try {
        const { data } = await this.$axios.get(`/admin/lessons/${this.selectedLessonId}/reference-pattern`);
        if (data) {
          this.hydratePatternFromReference(data);
        }
      } catch (error) {
        // در صورت نبود الگو نیاز به اعلان خطا نیست
      }
    },
    hydratePatternFromReference(pattern) {
      this.patternForm.lengthMeasures = pattern.length_measures || 1;
      this.patternForm.events = (pattern.events || []).map((event) => {
        const normalized = {
          event_index: event.event_index,
          measure: event.measure || 1,
          beat: event.beat || 1,
          subdivision: event.subdivision || 0,
          stroke_type: event.stroke_type || "R",
          accented: Boolean(event.accented),
          roll_type: event.roll_type || null,
          expected_velocity: event.expected_velocity || 100,
          duration_ms: event.duration_ms || 240,
          time_offset_ms: event.time_offset_ms ?? 0,
        };
        this.extendOptionsForEvent(normalized);
        return normalized;
      });
      this.patternForm.blueprint = null;
    },
    syncComposerDefaults() {
      this.eventComposer.measure = 1;
      this.eventComposer.beat = 1;
      this.eventComposer.subdivision = 0;
    },
    resetEvents() {
      this.patternForm.events = [];
      this.patternForm.blueprint = null;
    },
    generateEventsFromBlueprint() {
      if (!this.patternForm.blueprint) {
        return;
      }
      const generatorMap = {
        "steady-pulse": this.generateSteadyPulse,
        "syncopated-flow": this.generateSyncopatedFlow,
        "flam-intro": this.generateFlamIntro,
        "crescendo-roll": this.generateCrescendoRoll,
      };
      const generator = generatorMap[this.patternForm.blueprint];
      if (generator) {
        generator();
      }
    },
    generateSteadyPulse() {
      const events = [];
      const measures = this.patternForm.lengthMeasures;
      const beats = this.lessonForm.beatsPerMeasure;
      for (let measure = 1; measure <= measures; measure += 1) {
        for (let beat = 1; beat <= beats; beat += 1) {
          const accented = beat === 1;
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: 0,
            stroke: beat % 2 === 0 ? "L" : "R",
            accented,
            velocity: accented ? 120 : 100,
            duration: 260,
          }));
        }
      }
      this.patternForm.events = this.reindexEvents(events);
    },
    generateSyncopatedFlow() {
      const events = [];
      const measures = this.patternForm.lengthMeasures;
      const beats = this.lessonForm.beatsPerMeasure;
      const subdivision = this.lessonForm.subdivisionPerBeat;
      for (let measure = 1; measure <= measures; measure += 1) {
        for (let beat = 1; beat <= beats; beat += 1) {
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: 0,
            stroke: beat % 2 === 0 ? "L" : "R",
            accented: beat === 1,
            velocity: beat === 1 ? 118 : 102,
            duration: 240,
          }));
          const syncSubdivision = Math.max(1, Math.floor(subdivision / 2));
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: syncSubdivision,
            stroke: beat % 2 === 0 ? "R" : "L",
            accented: beat === beats,
            velocity: beat === beats ? 110 : 95,
            duration: 200,
            offset: beat === beats ? -15 : 0,
          }));
        }
      }
      this.patternForm.events = this.reindexEvents(events);
    },
    generateFlamIntro() {
      const events = [];
      const measures = this.patternForm.lengthMeasures;
      const beats = this.lessonForm.beatsPerMeasure;
      for (let measure = 1; measure <= measures; measure += 1) {
        events.push(this.createEvent({
          measure,
          beat: 1,
          subdivision: 0,
          stroke: "R",
          accented: true,
          velocity: 122,
          roll: "flam",
          duration: 320,
        }));
        for (let beat = 1; beat <= beats; beat += 1) {
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: 0,
            stroke: beat % 2 === 0 ? "L" : "R",
            accented: beat === 3,
            velocity: beat === 3 ? 118 : 100,
            duration: 260,
          }));
        }
      }
      this.patternForm.events = this.reindexEvents(events);
    },
    generateCrescendoRoll() {
      const events = [];
      const measures = this.patternForm.lengthMeasures;
      const beats = this.lessonForm.beatsPerMeasure;
      for (let measure = 1; measure <= measures; measure += 1) {
        for (let beat = 1; beat <= beats; beat += 1) {
          const intensity = measure / measures;
          const velocity = 90 + Math.round(intensity * 35);
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: 0,
            stroke: beat % 2 === 0 ? "L" : "R",
            accented: beat === 1,
            velocity,
            duration: 260,
            roll: beat === beats ? "buzz" : null,
          }));
          events.push(this.createEvent({
            measure,
            beat,
            subdivision: Math.min(1, this.lessonForm.subdivisionPerBeat - 1),
            stroke: beat % 2 === 0 ? "R" : "L",
            accented: false,
            velocity: velocity - 10,
            duration: 200,
            offset: 15,
          }));
        }
      }
      this.patternForm.events = this.reindexEvents(events);
    },
    createEvent({
      measure,
      beat,
      subdivision,
      stroke,
      accented = false,
      velocity = 100,
      duration = 240,
      offset = 0,
      roll = null,
    }) {
      return {
        event_index: 0,
        measure,
        beat,
        subdivision,
        stroke_type: stroke,
        accented,
        roll_type: roll,
        expected_velocity: velocity,
        duration_ms: duration,
        time_offset_ms: offset,
      };
    },
    reindexEvents(events) {
      return events.map((event, index) => {
        const normalized = {
          ...event,
          event_index: index,
        };
        this.extendOptionsForEvent(normalized);
        return normalized;
      });
    },
    extendOptionsForEvent(event) {
      if (event.expected_velocity && !this.velocityOptions.includes(event.expected_velocity)) {
        this.velocityOptions = [...this.velocityOptions, event.expected_velocity].sort((a, b) => a - b);
      }
      if (event.duration_ms && !this.durationOptions.includes(event.duration_ms)) {
        this.durationOptions = [...this.durationOptions, event.duration_ms].sort((a, b) => a - b);
      }
      if (!this.offsetOptions.some((item) => item.value === event.time_offset_ms)) {
        this.offsetOptions = [
          ...this.offsetOptions,
          { text: `${event.time_offset_ms}ms`, value: event.time_offset_ms },
        ];
      }
      if (event.stroke_type && !this.strokeTypeOptions.some((item) => item.value === event.stroke_type)) {
        this.strokeTypeOptions = [
          ...this.strokeTypeOptions,
          { text: event.stroke_type, value: event.stroke_type },
        ];
      }
      if (event.roll_type && !this.rollTypeOptions.some((item) => item.value === event.roll_type)) {
        this.rollTypeOptions = [
          ...this.rollTypeOptions,
          { title: event.roll_type, value: event.roll_type },
        ];
      }
    },
    addEventFromComposer() {
      const newEvent = {
        event_index: this.patternForm.events.length,
        measure: this.eventComposer.measure,
        beat: this.eventComposer.beat,
        subdivision: this.eventComposer.subdivision,
        stroke_type: this.eventComposer.strokeType,
        accented: this.eventComposer.accented,
        roll_type: this.eventComposer.rollType,
        expected_velocity: this.eventComposer.expectedVelocity,
        duration_ms: this.eventComposer.duration,
        time_offset_ms: this.eventComposer.offset,
      };
      this.extendOptionsForEvent(newEvent);
      this.patternForm.events = this.reindexEvents([...this.patternForm.events, newEvent]);
    },
    moveEventUp(index) {
      if (index === 0) {
        return;
      }
      const events = [...this.patternForm.events];
      [events[index - 1], events[index]] = [events[index], events[index - 1]];
      this.patternForm.events = this.reindexEvents(events);
    },
    moveEventDown(index) {
      if (index === this.patternForm.events.length - 1) {
        return;
      }
      const events = [...this.patternForm.events];
      [events[index + 1], events[index]] = [events[index], events[index + 1]];
      this.patternForm.events = this.reindexEvents(events);
    },
    removeEvent(index) {
      const events = [...this.patternForm.events];
      events.splice(index, 1);
      this.patternForm.events = this.reindexEvents(events);
    },
    mapStrokeLabel(value) {
      const option = this.strokeTypeOptions.find((item) => item.value === value);
      return option ? option.text : value;
    },
    buildLessonPayload() {
      const groove = this.grooveOptions.find((item) => item.value === this.lessonForm.groove);
      const title = this.computedLessonTitle || (groove?.title ?? "قطعه کوبه‌ای");
      return {
        title,
        description: groove?.description ?? null,
        tempo_bpm: this.lessonForm.tempo,
        time_signature: this.lessonForm.timeSignature,
        beats_per_measure: this.lessonForm.beatsPerMeasure,
        subdivision_per_beat: this.lessonForm.subdivisionPerBeat,
        grid_settings: {
          swing_ratio: this.lessonForm.swingRatio,
        },
        metadata: {
          groove: this.lessonForm.groove,
          generated: true,
        },
      };
    },
    buildPatternPayload(lessonId) {
      return {
        name: this.computedPatternName || this.selectedLessonTitle || `Pattern-${lessonId}`,
        length_measures: this.patternForm.lengthMeasures,
        grid: {
          tempo_bpm: this.lessonForm.tempo,
          time_signature: this.lessonForm.timeSignature,
          beats_per_measure: this.lessonForm.beatsPerMeasure,
          subdivision_per_beat: this.lessonForm.subdivisionPerBeat,
          swing_ratio: this.lessonForm.swingRatio,
        },
        articulation_rules: this.buildArticulationRules(),
        events: this.patternForm.events.map((event, index) => ({
          event_index: index,
          measure: event.measure,
          beat: event.beat,
          subdivision: event.subdivision,
          stroke_type: event.stroke_type,
          accented: event.accented,
          roll_type: event.roll_type,
          expected_velocity: event.expected_velocity,
          duration_ms: event.duration_ms,
          time_offset_ms: event.time_offset_ms,
        })),
      };
    },
    buildArticulationRules() {
      const rules = {};
      if (this.patternForm.accentProfile === "strong-downbeat") {
        rules.accent_velocity_boost = 15;
      }
      if (this.patternForm.accentProfile === "syncopated") {
        rules.syncopation_emphasis = true;
      }
      if (this.patternForm.velocityProfile === "dynamic") {
        rules.dynamics_curve = "crescendo";
      }
      if (this.patternForm.velocityProfile === "soft") {
        rules.dynamics_curve = "piano";
      }
      return Object.keys(rules).length ? rules : null;
    },
    async createPiece() {
      if (!this.canSubmit) {
        return;
      }
      this.saving = true;
      try {
        let lessonId = this.selectedLessonId;
        if (!lessonId) {
          const lessonPayload = this.buildLessonPayload();
          const { data } = await this.$axios.post("/admin/lessons", lessonPayload);
          lessonId = data.id;
          this.selectedLessonId = lessonId;
          this.selectedLessonTitle = data.title;
          this.lessonOptions = [data, ...this.lessonOptions.filter((item) => item.id !== data.id)];
        }
        const patternPayload = this.buildPatternPayload(lessonId);
        await this.$axios.post(`/admin/lessons/${lessonId}/reference-pattern`, patternPayload);
        this.openSnackbar("قطعه با موفقیت ذخیره شد");
        this.step = 3;
      } catch (error) {
        if (error.response?.data?.errors) {
          this.showValidationErrors(error);
        } else {
          this.openSnackbar("خطایی در ذخیره‌سازی رخ داد", "#d40d45");
        }
      } finally {
        this.saving = false;
      }
    },
    resetWizard() {
      this.step = 1;
      this.selectedLessonId = null;
      this.selectedLessonTitle = "";
      this.lessonForm = {
        groove: null,
        tempo: null,
        timeSignature: null,
        beatsPerMeasure: null,
        subdivisionPerBeat: null,
        swingRatio: null,
      };
      this.patternForm = {
        blueprint: null,
        lengthMeasures: 1,
        accentProfile: "balanced",
        velocityProfile: "medium",
        events: [],
      };
      this.syncComposerDefaults();
    },
  },
};
</script>

<style scoped>
.music-piece-builder {
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
  min-height: 100%;
}

.page-card {
  border-radius: 16px;
}

.page-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a237e;
}

.selection-card {
  border-radius: 16px;
  background: #ffffff;
}

.primary-card {
  background: linear-gradient(135deg, rgba(105, 108, 255, 0.08), rgba(0, 191, 165, 0.08));
}

.card-title {
  font-weight: 600;
  color: #283593;
}

.transparent-stepper {
  background-color: transparent;
}

.event-composer {
  border-radius: 12px;
  background: rgba(248, 248, 255, 0.8);
}

.event-table-wrapper {
  max-height: 360px;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid rgba(38, 50, 56, 0.08);
}

.event-table th {
  background: rgba(105, 108, 255, 0.1);
  color: #1a237e;
  font-weight: 600;
}

.event-table td {
  background: #ffffff;
  vertical-align: middle;
}

.action-cell {
  white-space: nowrap;
}

.empty-state {
  color: rgba(0, 0, 0, 0.54);
}

.event-preview-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.event-preview-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 20px;
  background: rgba(105, 108, 255, 0.12);
  color: #1a237e;
  font-size: 0.85rem;
}

.event-index {
  font-weight: 700;
}

.event-measure,
.event-beat {
  font-weight: 600;
}

.timeline-label {
  font-weight: 600;
  color: #283593;
  margin-bottom: 12px;
}

.muted-text {
  color: rgba(0, 0, 0, 0.54);
  font-size: 0.85rem;
}

@media (max-width: 960px) {
  .page-card {
    padding: 16px;
  }
}
</style>
